import { createSelector } from 'reselect';
import { selectPagination } from 'redux/helpers/pagination';
import get from 'lodash/get';
import memoize from 'lodash/memoize';

// Stable identity, so selectors depending on an empty list do not recompute forever
const fallbackArray = [];

const getProfile = ({ workspace: { profile } }) => profile;

const getChannelEntities = ({ entities: { channels } }) => channels;
const getWorkspaceChannelIds = ({ workspace: { channelIds } }) => channelIds;

const getWorkspaceChannels = createSelector(
  [getChannelEntities, getWorkspaceChannelIds],
  (entities, channelIds) => channelIds.map(id => entities[id]),
);

const areWorkspaceChannelsLoaded = ({ workspace: { channelsLoaded } }) => channelsLoaded;

const getWorkspaceChannel = ({ entities: { channels } }, id) => channels[id];

const getChannelSelector = createSelector(
  [getChannelEntities],
  (entities) => memoize(channelId => entities?.[channelId]),
);

const getGroupIds = ({ workspace: { groups } }) => groups;
const getGroups = ({ entities: { groups }, workspace: { groups: ids } }) => ids.map(id => groups[id]);
const areGroupsLoaded = ({ workspace: { groupsLoaded } }) => groupsLoaded;

const getMemberIds = ({ workspace: { members } }) => members.all;

const getMemberEntities = ({ entities: { members } }) => members;

const getMembers = createSelector([getMemberIds, getMemberEntities], (memberIds, entities) => memberIds.map(id => entities[id]));

const getAllMemberIdsPagination = ({ workspace: { members: { all } } }) => selectPagination(all, null, 'memberIds');

const getPendingMemberIds = ({ workspace: { members: { pending } } }) => pending.memberIds || fallbackArray;
const getPendingMembersPagination = ({ workspace: { members: { pending } } }) => selectPagination(pending, null, 'memberIds');

// Read the status back off the entity, so a member approved from the list leaves it
// without waiting for a refetch. Rejected ones are dropped from the id list itself.
const getPendingMembers = createSelector(
  [getPendingMemberIds, getMemberEntities],
  (memberIds, entities) => memberIds.map(id => entities[id]).filter(member => member?.status === 'PENDING'),
);

const getMemberSelector = createSelector(
  [getMemberEntities],
  members => memoize(
    memberId => members?.[memberId],
  ),
);

const getMember = ({ entities: { members } }, id) => members?.[id];

const getLabelEntities = ({ entities: { labels } }) => labels;

const getLabelMembers = ({ workspace: { labelMembers } }) => labelMembers;
const getLabelMemberIds = createSelector(
  [getLabelMembers],
  labelMembers => memoize(
    labelId => labelMembers?.[labelId] ?? [],
  ),
);

const getLabels = createSelector([getLabelEntities], (labels) => Object.values(labels)?.filter(v => v.type === 'DEFAULT'));
const getSortedLabels = createSelector([getLabels], (labels) => labels.sort((a, b) => a.name.localeCompare(b.name)));
const getSortedLabelNames = createSelector([getSortedLabels], (labels) => labels.map(({ name }) => name));

const getAnalyticalLabels = createSelector([getLabelEntities], (labels) => Object.values(labels)?.filter(v => v.type === 'ANALYTICAL'));
const getSortedAnalyticalLabels = createSelector([getAnalyticalLabels], (labels) => labels.sort((a, b) => a.name.localeCompare(b.name)));
const getSortedAnalyticalLabelNames = createSelector([getSortedAnalyticalLabels], (labels) => labels.map(({ name }) => name));

const getLearningLabels = createSelector([getLabelEntities], (labels) => Object.values(labels)?.filter(v => v.type === 'LEARNING') ?? []);
const getSortedLearningLabels = createSelector([getLearningLabels], (labels) => labels.sort((a, b) => a.name.localeCompare(b.name)));
const getSortedLearningLabelNames = createSelector([getSortedLearningLabels], (labels) => labels.map(({ name }) => name));

const getLabelSelector = createSelector(
  [getLabelEntities],
  labels => memoize(
    (labelId) => labels?.[labelId],
  ),
);

const getAnalyticalLabelsLoading = ({ workspace: { analyticalLabelsLoading } }) => analyticalLabelsLoading;


const getInvitations = ({ workspace: { invitations } }) => invitations.items;

const getInvitationsMaxActive = ({ workspace: { invitations } }) => invitations.maxActive;

const getInvitationCreating = ({ workspace: { invitations } }) => invitations.creating;

const getLastCreatedInvitationId = ({ workspace: { invitations } }) => invitations.lastCreatedId;

const getHome = ({ workspace: { home } }) => home;

const getHomeWidgetsSelector = createSelector(
  [getHome],
  ({ workspaceId, widgets }) => memoize(id => (id === workspaceId ? widgets : undefined)),
);

const getHomeLayoutKey = ({ workspace: { home } }) => home.layoutKey;
const getSettings = ({ workspace: { settings } }) => settings;


const getUser = ({ workspace: { user } }) => user;

const getAllChallenges = ({ workspace: { challenges } }) => selectPagination(challenges, 'all', 'challengeIds');
const getActiveChallenges = ({ workspace: { challenges } }) => selectPagination(challenges, 'active', 'challengeIds');
const getFinishedChallenges = ({ workspace: { challenges } }) => selectPagination(challenges, 'finished', 'challengeIds');

const getChallenge = ({ entities: { challenges } }, id) => challenges[id];

const getChallengeTemplatesEntities = ({ entities: { templates } }) => templates;
const getChallengeTemplates = createSelector([getChallengeTemplatesEntities], entities => Object.values(entities || {}));

const getWorkspaceAnalyticsLoading = ({ workspace: { analytics } }, type) => analytics[type].loading;
const getWorkspaceAnalytics = ({ workspace: { analytics } }, type, period, labelId = null) => get(analytics, `${type}.${period}.${labelId || 'all'}`, undefined);

const getSearchPagination = ({ workspace: { search } }) => selectPagination(search, null, 'results');

// normalizr drops a hit's `data` key when its entity carries no usable id, leaving nothing to look up
const getSearch = createSelector(
  [getSearchPagination],
  search => ({ ...search, results: search.results.filter(({ data }) => data != null) }),
);

const getSearchAutocomplete = ({ workspace: { search: { autocomplete } } }) => autocomplete;

const getRoles = ({ workspace: { members: { roles: { all } } } }) => all;
const getRoleEntities = ({ entities: { roles } }) => roles;
const getRoleSelector = createSelector([getRoleEntities], roles => memoize((roleId) => roles?.[roleId]));
const getRolesLoading = ({ workspace: { members: { roles: { loading } } } }) => loading;

const getContactDirectoryItemList = ({ workspace: { contactDirectory: { list } } }) => selectPagination(list, null, 'items');
const getContactDirectoryMembersTree = ({ workspace: { contactDirectory: { tree } } }) => selectPagination(tree, null, 'items');

const getContactEntities = ({ entities: { contacts } }) => contacts;
const getContactSelector = createSelector([getContactEntities], contacts => memoize((contactId) => contacts?.[contactId]));

const getContactDirectoryItemSelector = createSelector(
  [getMemberSelector, getContactSelector],
  (memberSelector, contactSelector) => memoize((itemType, itemId) => {
    switch (itemType) {
      case 'member': return memberSelector(itemId);
      case 'contact': return contactSelector(itemId);
      default: return null;
    }
  }, (itemType, itemId) => itemType + itemId),
);

const getStructuralUnits = ({ workspace: { structuralUnits } }) => structuralUnits.data;
const getStructuralUnitsStatus = ({ workspace: { structuralUnits } }) => structuralUnits.status;

export default {
  getWorkspaceChannels,
  areWorkspaceChannelsLoaded,
  getWorkspaceChannel,

  getChannelSelector,

  getGroupIds,
  getGroups,
  areGroupsLoaded,

  getLabels,
  getSortedLabels,
  getSortedLabelNames,
  getLabelSelector,

  getLabelMemberIds,

  getMember,
  getMemberIds,
  getAllMemberIdsPagination,
  getPendingMembers,
  getPendingMembersPagination,
  getMembers,
  getMemberSelector,

  getLearningLabels,
  getSortedLearningLabels,
  getSortedLearningLabelNames,

  getAnalyticalLabels,
  getSortedAnalyticalLabels,
  getSortedAnalyticalLabelNames,
  getAnalyticalLabelsLoading,

  getInvitations,
  getInvitationsMaxActive,
  getInvitationCreating,
  getLastCreatedInvitationId,

  getHomeWidgetsSelector,
  getHomeLayoutKey,
  getSettings,

  getUser,

  getProfile,

  getAllChallenges,
  getActiveChallenges,
  getFinishedChallenges,
  getChallenge,
  getChallengeTemplates,

  getWorkspaceAnalytics,
  getWorkspaceAnalyticsLoading,

  getSearch,
  getSearchAutocomplete,

  getRoles,
  getRolesLoading,
  getRoleSelector,

  getContactDirectoryItemList,
  getContactDirectoryMembersTree,
  getContactDirectoryItemSelector,

  getStructuralUnits,
  getStructuralUnitsStatus,
};
